import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";

import Notification from "./Header/HeaderContent/Notification";
import MobileHeaderMenu from "./Header/HeaderContent/MobileHeaderMenu";
import AdminNotificationSection from "components/AdminNotificationSection";
import GlobalPointsDisplay from "./Header/HeaderContent/GlobalPointsDisplay";
import QuickNavButton from "./Header/HeaderContent/QuickNavButton";
import NavUser from "./Drawer/DrawerContent/NavUser";
import { useGlobalSearch } from "hooks/useGlobalSearch";

import { handlerDrawerOpen } from "api/menu";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel, isFullAdmin } from "utils/adminAccess";
import { headerIconButtonSx, V2 } from "./Header/headerV2Tokens";
import { Search } from "lucide-react";

/** Remix RiMenuUnfoldLine — open sidebar (mobile) */
function MenuUnfoldIcon({ size = 22, color = "currentColor" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={color}
      aria-hidden
    >
      <path d="M21 17.9995V19.9995H3V17.9995H21ZM17.4038 3.90332L22 8.49951L17.4038 13.0957L15.9896 11.6815L19.1716 8.49951L15.9896 5.31753L17.4038 3.90332ZM12 10.9995V12.9995H3V10.9995H12ZM12 3.99951V5.99951H3V3.99951H12Z" />
    </svg>
  );
}

// ==============================|| V2 SCROLLABLE PAGE CHROME ||============================== //

/**
 * Top action row for V2 shell routes — lives inside scrollable main (not a fixed AppBar).
 * Left: open drawer on mobile. Desktop open control lives in the mini sidebar.
 */
export default function PageChrome() {
  const { user } = useAuth();
  const isAdmin = canAccessAdminPanel(user);
  const downLG = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const downXS = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { open: searchOpen, toggle: toggleSearch } = useGlobalSearch();

  const searchButton = (
    <IconButton
      aria-label="Rechercher"
      onClick={toggleSearch}
      color="secondary"
      size="large"
      sx={headerIconButtonSx(searchOpen)}
    >
      <Search
        size={20}
        color={searchOpen ? V2.accent : V2.ink}
        strokeWidth={1.7}
      />
    </IconButton>
  );

  return (
    <>
      <Box
        className="dashboard-v2-page-chrome"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 1, sm: 1.5 },
          width: "min(1180px, 100%)",
          maxWidth: 1180,
          mx: "auto",
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 2, sm: 2.5 },
          mb: { xs: 1.5, sm: 2.5 },
          boxSizing: "border-box",
          fontFamily: V2.fontBody,
        }}
      >
        {downLG ? (
          <IconButton
            aria-label="Ouvrir le menu"
            onClick={() => handlerDrawerOpen(true)}
            edge="start"
            color="secondary"
            size="large"
            sx={headerIconButtonSx(false)}
          >
            <MenuUnfoldIcon size={22} color={V2.ink} />
          </IconButton>
        ) : (
          <Box aria-hidden sx={{ width: 0, height: 0 }} />
        )}

        <Stack
          direction="row"
          spacing={{ xs: 0.75, sm: 1 }}
          alignItems="center"
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          {!downXS && (
            <>
              {searchButton}
              <GlobalPointsDisplay />
              <QuickNavButton isAdmin={isAdmin} />
              <AdminNotificationSection />
              {isFullAdmin(user) ? null : <Notification />}
              <NavUser />
            </>
          )}
          {downXS && (
            <>
              {searchButton}
              <MobileHeaderMenu />
            </>
          )}
        </Stack>
      </Box>
    </>
  );
}
